import moment from "moment";
import { Form, Modal, Row, Col } from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GetUserByDepartmentId } from "../../api/UserApi";
import { CriticalSituationForm } from "./CriticalSituationForm";
import AdnMessageSuccess from "../common/adnMessage/AdnMessageSuccess";
import AdnMessageError from "../common/adnMessage/AdnMessageError";
import AdnDatePicker from "../common/adnDatePicker/AdnDatePicker";
import AdnFormOptions from "../common/adnFormOptions/AdnFormOptions";
import AdnTextArea from "../common/adnTextArea/AdnTextArea";
import AdnSwitch from "../common/adnSwitch/AdnSwitch";
import {
  appTitle,
  dateFormat,
  Departement,
  CriticalSituationStatus,
  getCriticalSituationOptions,
  EmailListName,
} from "../common/GeneralConfigurations";
import { AppRoutes } from "../../AppRoutes";
import { getEmployeeByName, getEmployeeById } from "../../api/EmployeeApi";
import {
  getCriticalSituation,
  addCriticalSituation,
  updateCriticalSituation,
} from "../../api/CriticalSituationApi";
import {
  getCriticalSituationFollowups,
  getCriticalSituationFollowupById,
  addCriticalSituationFollowup,
  updateCriticalSituationFollowup,
} from "../../api/CriticalSituationFollowupApi";
import { userHasRolePermission, getUserEmail } from "../../api/ApiUtils";
import { getEmailList } from "../../api/EmailListApi";

const ManageCriticalSituationPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const employeeIdFromParams = searchParams.get("employeeId");
  const [isManagementHumanCapital, setIsManagementHumanCapital] =
    useState(false);
  const [form] = Form.useForm();
  const [followupForm] = Form.useForm();
  const [initValues, setInitValues] = useState({
    id: id,
    origin: searchParams.get("origin"),
    statusId: CriticalSituationStatus.FollowingUp,
    reportDate: moment(),
    employeeId: employeeIdFromParams || null,
  });
  const [followupInitValues, setFollowupInitValues] = useState({
    criticalSituationId: id,
  });
  const [statusOptions, setStatusOptions] = useState([]);
  const [managers, setManagers] = useState([]);
  const [employeesList, setEmployeesList] = useState([]);
  const [readOnly, setReadOnly] = useState(false);
  const [employeeReadOnly, setEmployeeReadOnly] = useState(
    !!employeeIdFromParams
  );
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showNextFollowupDate, setShowNextFollowupDate] = useState(true);
  const [criticalSituationFollowups, setCriticalSituationFollowups] = useState(
    []
  );

  const title = `${id === undefined ? "Add" : "Edit"} Critical Situation`;

  useEffect(() => {
    const fetchData = async () => {
      document.title = `${appTitle} - ${title}`;

      if (userHasRolePermission("Manager")) {
        navigate(AppRoutes.forbidden.path);
      }

      userIsManagementHumanCapital();
      setStatusOptions(getCriticalSituationOptions());
      fetchManagers();
      if (+id > 0) {
        await getCriticalSituationById(id);
      } else if (initValues.employeeId) {
        await getEmployeeByIdAndSetForm(initValues.employeeId);
      }
    };

    fetchData();
  }, []);

  const userIsManagementHumanCapital = async () => {
    await getEmailList({ email: getUserEmail() })
      .then((res) => {
        if (res.status === 200) {
          let exist = res.data.filter(
            (x) =>
              x.name === EmailListName.LisbonHumanCapitalManagement ||
              x.name === EmailListName.PortoHumanCapitalManagement
          );
          setIsManagementHumanCapital(exist.length === 1);
        } else {
          AdnMessageError("Could not get management HC.");
          setIsManagementHumanCapital(false);
        }
      })
      .catch((e) => {
        AdnMessageError("Could not get management HC.");
        setIsManagementHumanCapital(false);
      });
  };

  const fetchEmployees = async (name) => {
    await getEmployeeByName(name)
      .then((res) => {
        if (res.status === 200) {
          setEmployeesList(res.data);
        } else {
          AdnMessageError("Could not get consultants.");
        }
      })
      .catch((e) => {
        AdnMessageError("Could not get the consultants.");
      });
  };

  const getEmployeeByIdAndSetForm = async (employeeId) => {
    await getEmployeeById(employeeId)
      .then((res) => {
        if (res.status === 200) {
          const employee = res.data;
          form.setFieldValue("employeeId", employee.id);
          form.setFieldValue("employee", employee.name);
          setEmployeesList([employee]);
        } else {
          AdnMessageError("Could not get employee.");
        }
      })
      .catch((e) => {
        AdnMessageError("Could not get the employee.");
      });
  };

  const fetchManagers = async () => {
    await GetUserByDepartmentId(Departement.Manager)
      .then((res) => {
        if (res.status === 200) {
          setManagers(res.data.result.filter((u) => !u.deleted));
        } else {
          AdnMessageError("Could not get list of managers.");
        }
      })
      .catch(() => {
        AdnMessageError("Could not get list of managers.");
      });
  };

  const handleSelectEmployee = (employee, option) => {
    form.setFieldValue("employeeId", option.key);
    form.setFieldValue("employee", employee);
  };

  const handleSearchEmployee = async (name) => {
    name.length >= 3 ? await fetchEmployees(name) : setEmployeesList([]);
  };

  const handleCancel = () => {
    navigate(AppRoutes.criticalSituationsList.path);
  };

  const handleSubmitForm = (values) => {
    console.log(values);
    id === undefined ? add(values) : update(values);
  };

  const prepareDate = (data) => {
    const newData = {
      ...data,
      id: id,
      reportDate: moment(data.reportDate).format("YYYY-MM-DD"),
      followupDate: data.followupDate
        ? moment(data.followupDate).format("YYYY-MM-DD")
        : null,
    };
    return newData;
  };

  const getCriticalSituationById = async (id) => {
    setSubmitLoading(true);
    await getCriticalSituation(id)
      .then(async (res) => {
        if (res.status === 200) {
          const employeeName = res.data.employee;

          setEmployeeReadOnly(true);

          form.setFieldValue("employeeId", res.data.employeeId);
          form.setFieldValue("employee", employeeName);

          setInitValues((prev) => ({
            ...prev,
            employee: employeeName,
            employeeId: res.data.employeeId,
          }));

          setEmployeesList([{ id: res.data.employeeId, name: employeeName }]);

          form.setFieldValue("managerId", res.data.managerId);
          form.setFieldValue("origin", res.data.origin);
          form.setFieldValue("reportDate", moment(res.data.reportDate));
          if (res.data.followupDate) {
            form.setFieldValue("followupDate", moment(res.data.followupDate));
          }
          form.setFieldValue("statusId", res.data.statusId);
          form.setFieldValue("description", res.data.description);
          form.setFieldValue("correctiveAction", res.data.correctiveAction);
          handleStatusChange(res.data.statusId);
          getCriticalSituationFollowupsListInfo(res.data.id);
        } else if (res.status === 400) {
          res.data.errors.map((e) => {
            AdnMessageError(e.message);
          });
        } else {
          AdnMessageError("Could not get critical situation.");
        }
      })
      .catch(() => {
        AdnMessageError("Could not get critical situation.");
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const getCriticalSituationFollowupsListInfo = async (criticalSituationId) => {
    await getCriticalSituationFollowups(criticalSituationId)
      .then((res) => {
        if (res.status === 200) {
          setCriticalSituationFollowups(res.data);
        } else if (res && res.data && res.data.errors) {
          res.data.errors.map((e) => {
            AdnMessageError(e.message);
          });
          setCriticalSituationFollowups([]);
        } else {
          AdnMessageError("Could not get critical follow ups.");
        }
      })
      .catch(() => {
        AdnMessageError("Could not get critical situation follow ups.");
      });
  };

  const add = async (data) => {
    setSubmitLoading(true);
    await addCriticalSituation(prepareDate(data))
      .then((res) => {
        if (res.status === 201) {
          navigate(AppRoutes.criticalSituationsList.path);
          AdnMessageSuccess("Critical Situation added.");
        } else if (res.status === 400) {
          res.data.errors.map((e) => {
            AdnMessageError(e.message);
          });
        } else {
          AdnMessageError("Could not add critical situation.");
        }
      })
      .catch(() => {
        AdnMessageError("Could not add critical situation.");
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const update = async (data) => {
    setSubmitLoading(true);
    await updateCriticalSituation(prepareDate(data))
      .then((res) => {
        if (res.status === 200) {
          navigate(AppRoutes.criticalSituationsList.path);
          AdnMessageSuccess("Critical Situation updated.");
        } else if (res.status === 400) {
          res.data.errors.map((e) => {
            AdnMessageError(e.message);
          });
        } else {
          AdnMessageError("Could not update critical situation.");
        }
      })
      .catch(() => {
        AdnMessageError("Could not update critical situation.");
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  const handleBackAddFollowUpModal = () => {
    setShowFollowUpModal(false);
  };

  const handleAddCriticalSituationFollowup = () => {
    followupForm.setFieldValue("id", 0);
    followupForm.setFieldValue("criticalSituationId", id);
    followupForm.setFieldValue("closeCriticalSituation", false);
    followupForm.setFieldValue("followupDate", moment());
    followupForm.setFieldValue("description", undefined);
    setShowFollowUpModal(true);
  };

  const validateFollowupDate = (form) => [
    {
      validator: async (_, followupDate) => {
        let nextFollowupDate = form.getFieldValue("nextFollowupDate");
        if (
          nextFollowupDate != null &&
          nextFollowupDate
            .startOf("day")
            .isSameOrBefore(followupDate.startOf("day"))
        ) {
          return Promise.reject("Must be less than the next follow up date");
        }
      },
    },
    { required: true, message: "Follow up date is required" },
  ];

  const handleSubmitFollowupForm = async (values) => {
    if (followupForm.getFieldValue("id") === 0) {
      const addValues = {
        ...values,
        criticalSituationId: id,
        followupDate: moment(values.followupDate).format("YYYY-MM-DD"),
        nextFollowupDate: values.nextFollowupDate
          ? moment(values.nextFollowupDate).format("YYYY-MM-DD")
          : null,
      };
      setSubmitLoading(true);
      await addCriticalSituationFollowup(addValues)
        .then((res) => {
          if (res.status === 201) {
            AdnMessageSuccess("Follow up added.");
            getCriticalSituationById(id);
            setShowFollowUpModal(false);
          } else if (res.status === 400) {
            res.data.errors.map((e) => {
              AdnMessageError(e.message);
            });
          } else {
            AdnMessageError("Could not add follow up.");
          }
        })
        .catch(() => {
          AdnMessageError("Could not add follow up.");
        })
        .finally(() => {
          setSubmitLoading(false);
        });
    } else {
      const updateValues = {
        ...values,
        criticalSituationId: id,
        followupDate: moment(values.followupDate).format("YYYY-MM-DD"),
      };
      setSubmitLoading(true);
      await updateCriticalSituationFollowup(updateValues)
        .then((res) => {
          if (res.status === 200) {
            AdnMessageSuccess("Follow up updated.");
            getCriticalSituationFollowupsListInfo(id);
            setShowFollowUpModal(false);
          } else if (res.status === 400) {
            res.data.errors.map((e) => {
              AdnMessageError(e.message);
            });
          } else {
            AdnMessageError("Could not update follow up.");
          }
        })
        .catch(() => {
          AdnMessageError("Could not update follow up.");
        })
        .finally(() => {
          setSubmitLoading(false);
        });
    }
  };

  const handleStatusChange = (statusId) => {
    setReadOnly(statusId === CriticalSituationStatus.Closed);
  };

  const disableFollowupDateDate = (current) => {
    // accepts dates only from today forward
    return current && current <= moment().startOf("day");
  };

  const handleCloseCriticalSituation = (toClose) => {
    setShowNextFollowupDate(toClose === false);
    followupForm.setFieldValue("nextFollowupDate", null);
  };

  const handleEditCriticalSituationFollowup = async (id) => {
    setSubmitLoading(true);
    await getCriticalSituationFollowupById(id)
      .then((res) => {
        if (res.status === 200) {
          followupForm.setFieldValue("id", res.data.id);
          followupForm.setFieldValue(
            "criticalSituationId",
            res.data.criticalSituationId
          );
          followupForm.setFieldValue("description", res.data.description);
          followupForm.setFieldValue(
            "followupDate",
            moment(res.data.followupDate)
          );
          setShowFollowUpModal(true);
        } else if (res.status === 400) {
          res.data.errors.map((e) => {
            AdnMessageError(e.message);
          });
        } else {
          AdnMessageError("Could not get critical situation follow up.");
        }
      })
      .catch(() => {
        AdnMessageError("Could not get critical situation follow up.");
      })
      .finally(() => {
        setSubmitLoading(false);
      });
  };

  return (
    <>
      <CriticalSituationForm
        form={form}
        initValues={initValues}
        isManagementHumanCapital={isManagementHumanCapital}
        statusOptions={statusOptions}
        managers={managers}
        readOnly={readOnly}
        employeeReadOnly={employeeReadOnly}
        loading={loading}
        submitLoading={submitLoading}
        title={title}
        handleCancel={handleCancel}
        handleSubmitForm={handleSubmitForm}
        employeesList={employeesList}
        handleSelectEmployee={handleSelectEmployee}
        handleSearchEmployee={handleSearchEmployee}
        handleStatusChange={handleStatusChange}
        handleAddCriticalSituationFollowup={handleAddCriticalSituationFollowup}
        criticalSituationFollowups={criticalSituationFollowups}
        handleEditCriticalSituationFollowup={
          handleEditCriticalSituationFollowup
        }
      />

      <Modal
        title={`${
          followupForm.getFieldValue("id") > 0 ? "Edit" : "Add"
        } Follow Up`}
        open={showFollowUpModal}
        width={1000}
        footer={null}
      >
        <Form
          form={followupForm}
          name="followupForm"
          layout="vertical"
          onFinish={handleSubmitFollowupForm}
          initialValues={followupInitValues}
        >
          <Form.Item name="id" style={{ display: "none" }}>
            <input type="hidden" name="id" />
          </Form.Item>
          <Form.Item name="criticalSituationId" style={{ display: "none" }}>
            <input type="hidden" name="criticalSituationId" />
          </Form.Item>

          <Row gutter={16} style={{ paddingTop: "20px" }}>
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 24 }}
              lg={{ span: 24 }}
              xl={{ span: 5 }}
              xxl={{ span: 5 }}
              key="followupDate"
              className="gutter-row"
            >
              <AdnDatePicker
                label="Follow Up Date"
                name="followupDate"
                placeholder="Select a date"
                rules={validateFollowupDate(followupForm)}
                disabledDate={disableFollowupDateDate}
                format={dateFormat}
                allowClear={false}
                disabled={true}
              />
            </Col>
            {followupForm.getFieldValue("id") === 0 && (
              <>
                <Col
                  xs={{ span: 24 }}
                  sm={{ span: 24 }}
                  md={{ span: 24 }}
                  lg={{ span: 24 }}
                  xl={{ span: 5 }}
                  xxl={{ span: 5 }}
                  key="followupDate"
                  className="gutter-row"
                >
                  <AdnSwitch
                    name="closeCriticalSituation"
                    label="Close Critical Situation"
                    checkedText="Yes"
                    unCheckedText="No"
                    onChange={handleCloseCriticalSituation}
                  />
                </Col>
                {showNextFollowupDate && (
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 24 }}
                    lg={{ span: 24 }}
                    xl={{ span: 5 }}
                    xxl={{ span: 5 }}
                    key="followupDate"
                    className="gutter-row"
                  >
                    <AdnDatePicker
                      label="Next Follow Up Date"
                      name="nextFollowupDate"
                      placeholder="Select a date"
                      rules={[
                        {
                          required: true,
                          message: "Next follow up date is required",
                        },
                      ]}
                      disabledDate={disableFollowupDateDate}
                      format={dateFormat}
                    />
                  </Col>
                )}
              </>
            )}
          </Row>

          <Row gutter={16} style={{ paddingTop: "20px" }}>
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 24 }}
              lg={{ span: 24 }}
              xl={{ span: 24 }}
              xxl={{ span: 24 }}
              key="description"
              className="gutter-row"
            >
              <AdnTextArea
                name="description"
                label="Description"
                maxLength={500}
                showCount={true}
                placeholder="Follow up"
                rules={[{ required: true, message: "Description is required" }]}
                rows={5}
              />
            </Col>
          </Row>

          <Row gutter={16} style={{ paddingTop: "20px" }}>
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 24 }}
              lg={{ span: 24 }}
              xl={{ span: 24 }}
              xxl={{ span: 24 }}
              key="buttons"
              className="gutter-row"
            >
              <AdnFormOptions
                handleCancel={handleBackAddFollowUpModal}
                loading={loading}
              />
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default ManageCriticalSituationPage;
